import { useEffect, useMemo, useState } from "react";
import {
  Glyph,
  Guess,
  createStarterGuess,
  gameRules,
  getDailyAnswer,
  getPuzzleNumber,
  getTodayKey,
  glyphBank,
  isWinningScore,
  makeShareText,
  scoreGuess
} from "./game";

type PersistedGame = {
  guesses: Guess[];
  current: Glyph[];
};

const storagePrefix = "glyphgrid";

function getStorageKey(dateKey: string): string {
  return `${storagePrefix}:${dateKey}`;
}

function getStatsKey(): string {
  return `${storagePrefix}:stats`;
}

type Stats = {
  played: number;
  wins: number;
  streak: number;
  lastWinDate: string | null;
};

const emptyStats: Stats = {
  played: 0,
  wins: 0,
  streak: 0,
  lastWinDate: null
};

function App() {
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const selectedDateKey = getTodayKey(selectedDate);
  const todayKey = getTodayKey();
  const isToday = selectedDateKey === todayKey;
  const answer = useMemo(() => getDailyAnswer(selectedDate), [selectedDateKey]);
  const starterGuess = useMemo(() => createStarterGuess(answer), [answer]);
  const [current, setCurrent] = useState<Glyph[]>(starterGuess);
  const [guesses, setGuesses] = useState<Guess[]>([]);
  const [message, setMessage] = useState("Decode the hidden four-glyph pattern.");
  const [stats, setStats] = useState<Stats>(emptyStats);
  const [storageReady, setStorageReady] = useState(false);

  const latestGuess = guesses[guesses.length - 1];
  const solved = latestGuess ? isWinningScore(latestGuess.score) : false;
  const gameOver = solved || guesses.length >= gameRules.maxGuesses;
  const shareText = makeShareText(guesses, selectedDate);

  useEffect(() => {
    setStorageReady(false);
    const saved = localStorage.getItem(getStorageKey(selectedDateKey));
    const savedStats = localStorage.getItem(getStatsKey());

    if (saved) {
      const parsed = JSON.parse(saved) as PersistedGame;
      setGuesses(parsed.guesses);
      setCurrent(parsed.current);
    } else {
      setGuesses([]);
      setCurrent(starterGuess);
    }

    if (savedStats) {
      setStats(JSON.parse(savedStats) as Stats);
    }
    setMessage(
      isToday
        ? "Decode today's hidden four-glyph pattern."
        : `Archive puzzle #${getPuzzleNumber(selectedDate)} loaded.`
    );
    setStorageReady(true);
  }, [isToday, selectedDate, selectedDateKey, starterGuess]);

  useEffect(() => {
    if (!storageReady) {
      return;
    }
    localStorage.setItem(getStorageKey(selectedDateKey), JSON.stringify({ guesses, current }));
  }, [current, guesses, selectedDateKey, storageReady]);

  function chooseGlyph(slot: number, glyph: Glyph) {
    if (gameOver) {
      return;
    }

    setCurrent((items) => items.map((item, index) => (index === slot ? glyph : item)));
    setMessage(`${glyph.label} placed in slot ${slot + 1}.`);
  }

  function submitGuess() {
    if (gameOver) {
      return;
    }

    const uniqueIds = new Set(current.map((glyph) => glyph.id));
    if (uniqueIds.size !== gameRules.puzzleLength) {
      setMessage("Use four different glyphs before submitting.");
      return;
    }

    const score = scoreGuess(current, answer);
    const nextGuesses = [...guesses, { glyphs: current, score }];
    setGuesses(nextGuesses);

    if (isWinningScore(score)) {
      setMessage("Pattern cracked. The little constellation opens.");
      updateStats(true);
      return;
    }

    if (nextGuesses.length >= gameRules.maxGuesses) {
      setMessage(`Out of guesses. The pattern was ${answer.map((glyph) => glyph.icon).join(" ")}.`);
      updateStats(false);
      return;
    }

    setMessage(`${score.exact} exact, ${score.present} near. The grid is listening.`);
  }

  function updateStats(won: boolean) {
    const recordKey = `${getStatsKey()}:${selectedDateKey}`;
    const hasRecordedPuzzle = localStorage.getItem(recordKey);
    if (hasRecordedPuzzle) {
      return;
    }

    const nextStats: Stats = {
      played: stats.played + 1,
      wins: stats.wins + (won ? 1 : 0),
      streak: won && isToday ? stats.streak + 1 : stats.streak,
      lastWinDate: won && isToday ? todayKey : stats.lastWinDate
    };

    setStats(nextStats);
    localStorage.setItem(getStatsKey(), JSON.stringify(nextStats));
    localStorage.setItem(recordKey, "1");
  }

  async function copyShare() {
    if (!guesses.length) {
      setMessage("Make at least one guess before sharing.");
      return;
    }

    try {
      await navigator.clipboard.writeText(shareText);
      setMessage("Share card copied. Go make someone curious.");
    } catch {
      setMessage("Share text is ready, but clipboard access was blocked.");
    }
  }

  function resetLocalPuzzle() {
    setGuesses([]);
    setCurrent(starterGuess);
    setMessage("Local board reset. The answer stayed the same.");
  }

  function selectArchiveDate(date: Date) {
    setStorageReady(false);
    setSelectedDate(date);
  }

  return (
    <main>
      <Hero
        current={current}
        guesses={guesses}
        solved={solved}
        gameOver={gameOver}
        message={message}
        stats={stats}
        selectedDate={selectedDate}
        isToday={isToday}
        chooseGlyph={chooseGlyph}
        submitGuess={submitGuess}
        copyShare={copyShare}
        resetLocalPuzzle={resetLocalPuzzle}
      />
      <ArchivePreview selectedDateKey={selectedDateKey} selectArchiveDate={selectArchiveDate} />
      <HowItWorks />
      <WhoPlays />
      <FAQ />
      <Footer />
    </main>
  );
}

type HeroProps = {
  current: Glyph[];
  guesses: Guess[];
  solved: boolean;
  gameOver: boolean;
  message: string;
  stats: Stats;
  selectedDate: Date;
  isToday: boolean;
  chooseGlyph: (slot: number, glyph: Glyph) => void;
  submitGuess: () => void;
  copyShare: () => void;
  resetLocalPuzzle: () => void;
};

function Hero({
  current,
  guesses,
  solved,
  gameOver,
  message,
  stats,
  selectedDate,
  isToday,
  chooseGlyph,
  submitGuess,
  copyShare,
  resetLocalPuzzle
}: HeroProps) {
  return (
    <section className="hero">
      <nav className="nav" aria-label="Primary">
        <a className="brand" href="/">
          <span className="brand-mark">✦</span>
          GlyphGrid
        </a>
        <div className="nav-links">
          <a href="#play">Play</a>
          <a href="/how-to-play">How to play</a>
          <a href="/daily-brain-games">Brain games</a>
          <a href="#archive">Archive</a>
        </div>
      </nav>

      <div className="hero-grid">
        <div className="hero-copy">
          <p className="eyebrow">Daily puzzle #{getPuzzleNumber(selectedDate)} · Free browser game</p>
          <h1>A tiny ritual for brains that like patterns.</h1>
          <p className="lede">
            Crack one hidden emoji sequence each day. Exact clues glow orange, near clues glow blue, and the
            whole thing takes about three minutes.
          </p>

          <div className="hero-actions">
            <a className="button primary" href="#play">
              Play today's grid
            </a>
            <a className="button ghost" href="/how-to-play">
              Learn the rules
            </a>
          </div>

          <div className="metric-row" aria-label="Game details">
            <Metric label="Puzzle length" value="4 glyphs" />
            <Metric label="Guesses" value="7 tries" />
            <Metric label="Quick break" value="3 min" />
          </div>
        </div>

        <section className="game-card" id="play" aria-labelledby="game-title">
          <div className="game-card-top">
            <div>
              <p className="eyebrow compact">
                {isToday ? "Today's board" : selectedDate.toLocaleDateString("en", { month: "short", day: "numeric" })}
              </p>
              <h2 id="game-title">Find the hidden pattern</h2>
            </div>
            <div className="status-pill">{solved ? "Solved" : gameOver ? "Closed" : "Live"}</div>
          </div>

          <GuessBoard guesses={guesses} />
          <QuickGuide />
          <CurrentGuess current={current} chooseGlyph={chooseGlyph} />

          <div className="controls">
            <button className="button primary" type="button" onClick={submitGuess} disabled={gameOver}>
              Submit guess
            </button>
            <button className="button ghost" type="button" onClick={copyShare}>
              Copy result
            </button>
            <button className="button quiet" type="button" onClick={resetLocalPuzzle}>
              Reset local board
            </button>
          </div>

          <p className="message" role="status">
            {message}
          </p>

          <div className="stats-panel" aria-label="Your local stats">
            <Metric label="Played" value={String(stats.played)} />
            <Metric label="Wins" value={String(stats.wins)} />
            <Metric label="Streak" value={String(stats.streak)} />
          </div>
        </section>
      </div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function GuessBoard({ guesses }: { guesses: Guess[] }) {
  return (
    <div className="guess-board" aria-label="Previous guesses">
      {Array.from({ length: gameRules.maxGuesses }).map((_, index) => {
        const guess = guesses[index];
        return (
          <div className="guess-row" key={index}>
            <div className="guess-slots" aria-label={`Guess ${index + 1}`}>
              {Array.from({ length: gameRules.puzzleLength }).map((__, slot) => (
                <span className={`slot ${guess ? "filled" : ""}`} key={slot}>
                  {guess?.glyphs[slot]?.icon}
                </span>
              ))}
            </div>
            <div className="clues" aria-label={guess ? `${guess.score.exact} exact and ${guess.score.present} near` : "Empty"}>
              {guess ? (
                <>
                  <span className="clue exact">{guess.score.exact}</span>
                  <span className="clue present">{guess.score.present}</span>
                </>
              ) : (
                <span className="clue muted">–</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function QuickGuide() {
  return (
    <aside className="quick-guide" aria-label="Quick play guide">
      <div>
        <strong>How this works</strong>
        <span>Pick 4 glyphs, submit, then use the clue dots to move closer.</span>
      </div>
      <div className="guide-examples" aria-label="Clue examples">
        <span>
          <b className="mini-dot exact">2</b> exact: right glyph, right spot
        </span>
        <span>
          <b className="mini-dot present">1</b> near: right glyph, wrong spot
        </span>
      </div>
    </aside>
  );
}

function CurrentGuess({
  current,
  chooseGlyph
}: {
  current: Glyph[];
  chooseGlyph: (slot: number, glyph: Glyph) => void;
}) {
  const [activeSlot, setActiveSlot] = useState(0);

  return (
    <div className="current-builder">
      <div className="current-slots" aria-label="Current guess">
        {current.map((glyph, index) => (
          <button
            className={`slot current ${activeSlot === index ? "active" : ""}`}
            key={`${glyph.id}-${index}`}
            type="button"
            onClick={() => setActiveSlot(index)}
            aria-label={`Edit slot ${index + 1}, currently ${glyph.label}`}
          >
            {glyph.icon}
          </button>
        ))}
      </div>

      <div className="glyph-picker" aria-label="Glyph picker">
        {glyphBank.map((glyph) => (
          <button
            className="glyph-button"
            key={glyph.id}
            type="button"
            onClick={() => chooseGlyph(activeSlot, glyph)}
            aria-label={`Choose ${glyph.label}`}
          >
            <span>{glyph.icon}</span>
            <small>{glyph.label}</small>
          </button>
        ))}
      </div>
    </div>
  );
}

function ArchivePreview({
  selectedDateKey,
  selectArchiveDate
}: {
  selectedDateKey: string;
  selectArchiveDate: (date: Date) => void;
}) {
  const days = Array.from({ length: 7 }).map((_, index) => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - index);
    const dateKey = getTodayKey(date);
    const isToday = index === 0;
    const hasPlayed =
      typeof localStorage !== "undefined" ? Boolean(localStorage.getItem(getStorageKey(dateKey))) : false;
    return {
      date,
      dateKey,
      isToday,
      hasPlayed,
      puzzleNumber: getPuzzleNumber(date)
    };
  });

  return (
    <section className="section archive-section" id="archive">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Puzzle archive</p>
          <h2>Play today, or revisit the week.</h2>
        </div>
        <p>
          Missed a day? Pick a recent board from the archive. Your progress is saved in this browser, so you can return
          to an unfinished puzzle unless site data is cleared.
        </p>
      </div>
      <div className="archive-grid">
        {days.map((day) => (
          <button
            className={`archive-day ${day.isToday ? "today" : ""} ${selectedDateKey === day.dateKey ? "selected" : ""}`}
            key={day.dateKey}
            type="button"
            onClick={() => selectArchiveDate(day.date)}
          >
            <span>{day.isToday ? "Today" : day.date.toLocaleDateString("en", { weekday: "short" })}</span>
            <strong>#{day.puzzleNumber}</strong>
            <small>{day.hasPlayed ? "Played in this browser" : day.isToday ? "Available now" : "Archive preview"}</small>
          </button>
        ))}
        <article className="archive-upgrade">
          <span>Coming next</span>
          <strong>More daily boards</strong>
          <small>New puzzle styles and longer archives are planned.</small>
        </article>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="section" id="how-to-play">
      <p className="eyebrow">How to play</p>
      <div className="section-heading">
        <h2>Simple clues, satisfying deduction.</h2>
        <p>
          Every guess teaches you something. Use exact and near clues to narrow down the hidden four-glyph pattern.
        </p>
      </div>
      <div className="cards three">
        <ArticleCard title="Pick four glyphs" body="Use the palette to build a sequence. Each glyph can appear once." />
        <ArticleCard title="Read the clue lights" body="Orange means right glyph, right position. Blue means right glyph, wrong position." />
        <ArticleCard title="Come back tomorrow" body="One global puzzle per UTC day creates a simple sharing ritual and repeat visits." />
      </div>
    </section>
  );
}

function WhoPlays() {
  return (
    <section className="section ready-section" aria-labelledby="players-title">
      <div>
        <p className="eyebrow">Good for quick breaks</p>
        <h2 id="players-title">A small puzzle for spare minutes.</h2>
      </div>
      <div className="ready-list">
        <ArticleCard title="Morning warmup" body="Start the day with one short deduction puzzle before email and meetings." />
        <ArticleCard title="Coffee break" body="Play a full board in a few minutes without installing anything." />
        <ArticleCard title="Classroom prompt" body="Use the clues as a quick reasoning exercise for group discussion." />
        <ArticleCard title="Shareable result" body="Copy your result after a game and compare paths with friends." />
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section className="section dark" id="faq">
      <p className="eyebrow">Questions</p>
      <div className="timeline">
        <PlanStep label="Exact" title="What does exact mean?" body="The glyph is correct and already in the right position." />
        <PlanStep label="Near" title="What does near mean?" body="The glyph is in the answer, but it belongs in a different slot." />
        <PlanStep label="Daily" title="Is there a new puzzle every day?" body="Yes. A fresh puzzle appears each UTC day, and recent puzzles stay in the archive." />
        <PlanStep label="Privacy" title="Do I need an account?" body="No. Your progress is saved only in this browser and can be removed if site data is cleared." />
      </div>
    </section>
  );
}

function PlanStep({ label, title, body }: { label: string; title: string; body: string }) {
  return (
    <article className="plan-step">
      <span>{label}</span>
      <h3>{title}</h3>
      <p>{body}</p>
    </article>
  );
}

function ArticleCard({ title, body }: { title: string; body: string }) {
  return (
    <article className="article-card">
      <h3>{title}</h3>
      <p>{body}</p>
    </article>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <p>GlyphGrid Daily · A free daily emoji logic puzzle.</p>
      <div>
        <a href="#play">Play</a>
        <a href="/how-to-play">Rules</a>
        <a href="/daily-brain-games">Brain games</a>
        <a href="/classroom-brain-warmups">Teachers</a>
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
        <a href="mailto:hello@playglyph.online">Contact</a>
      </div>
    </footer>
  );
}

export default App;
